/**
 * Managed Apps API client — wraps `/apps/*` routes served by the
 * AppsFleet RestApi (base-path mapped onto api-${stage}.zibby.app/apps/).
 *
 *   listCatalog()                → { items: [{ appType, displayName, ... }] }
 *   getCatalogEntry(appType)     → { appType, displayName, ... }
 *   deploy({ appType, projectId, name? })  → 202 { instanceId, url, ... }
 *   listInstances()              → { items: [{ instanceId, status, ... }] }
 *   getInstance(instanceId)      → { ... }
 *   destroy(instanceId)          → { instanceId, status: 'stopped' }
 *
 * Catalog browse is public (no JWT). Everything else requires the user
 * to be signed in.
 */

import api from './api';

const BASE = '/apps';

export const Apps = {
  // ── catalog (no auth) ───────────────────────────────────────────────
  listCatalog() {
    return api.get(`${BASE}/catalog`);
  },

  getCatalogEntry(appType) {
    if (!appType) return Promise.reject(new Error('appType is required'));
    return api.get(`${BASE}/catalog/${encodeURIComponent(appType)}`);
  },

  /**
   * Rate an app. Polymorphic on the same DDB table the workflow
   * marketplace uses — backend `apps.rateApp` upserts the user's row
   * and recomputes the META aggregate atomically.
   * @param {string} appType
   * @param {{rating: number, review?: string}} opts
   */
  rate(appType, { rating, review } = {}) {
    if (!appType) return Promise.reject(new Error('appType is required'));
    return api.post(`${BASE}/catalog/${encodeURIComponent(appType)}/ratings`, { rating, review });
  },

  // ── instances ───────────────────────────────────────────────────────
  /**
   * List deployed app instances. Optionally scoped to a project; the
   * backend has a ProjectCreatedAtIndex GSI it queries when `projectId`
   * is set (newest first), otherwise it returns all instances under the
   * caller's account.
   * @param {{ projectId?: string }} opts
   */
  // `live: false` → server returns the STORED status fast (skips the ~1s ECS
  // reconcile). Used for the first paint; a follow-up call with live:true
  // refreshes the real status pills.
  listInstances({ projectId, live = true } = {}) {
    const params = [];
    if (projectId) params.push(`projectId=${encodeURIComponent(projectId)}`);
    if (!live) params.push('live=0');
    const qs = params.length ? `?${params.join('&')}` : '';
    return api.get(`${BASE}${qs}`);
  },

  getInstance(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}`);
  },

  /**
   * Deploy a marketplace app.
   * @param {string} appType    catalog id (e.g. "n8n")
   * @param {string} projectId  project to attach the instance to
   * @param {string} [name]     display name override
   * @param {'claude'|'codex'} [provider]  agent provider; default 'claude'
   * @param {'x86_64'|'arm64'} [architecture]  CPU arch; default = catalog's
   *   first listed arch (server picks). Omit to accept the operator-chosen
   *   default; only pass when the user explicitly picks one.
   * @param {string} [agentToken]  vendor-neutral ad-hoc per-deploy credential
   *   (claude sk-ant-… OR codex OpenAI sk-…). Used only for this deploy +
   *   snapshotted to the app row; NEVER written to project settings.
   * @returns 202 { instanceId, url, bridgeToken, mcpConfig, ... }
   */
  deploy({ appType, projectId, name, provider, architecture, subdomain, agentToken, tier } = {}) {
    if (!appType)   return Promise.reject(new Error('appType is required'));
    if (!projectId) return Promise.reject(new Error('projectId is required'));
    const body = { appType, projectId };
    if (name) body.name = name;
    // Per-deploy ad-hoc agent credential (vendor-neutral capture-only flow:
    // the project has no AI cred → the user pasted a token in the deploy modal).
    // Works for BOTH providers — claude (sk-ant-oat01-/sk-ant-api03-) AND codex
    // (OpenAI sk-…). MUST be forwarded or the backend 428s and the capture modal
    // loops. NEVER persisted client-side, NEVER written to project settings.
    if (agentToken) body.agentToken = agentToken;
    // Resource tier picked in the confirm modal.
    if (tier) body.tier = tier;
    // Optional vanity subdomain label; omitted when blank so the backend
    // assigns the random instanceId host (existing behaviour).
    if (subdomain) body.subdomain = subdomain;
    // Only send provider when the caller explicitly picked one — keeps
    // every pre-Codex consumer (CLI/MCP/old web bundle) request shape
    // byte-identical to backend default branch.
    if (provider) body.provider = provider;
    // Same gate for architecture: omit when the caller doesn't care, so
    // the server falls back to the catalog's first-listed arch (which is
    // also what the picker defaults to in the deploy modal — matches the
    // UX without trusting the client to mirror the catalog's preference).
    if (architecture) body.architecture = architecture;
    return api.post(`${BASE}/deploy`, body);
  },

  destroy(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    // Backend requires { confirm: true } — same shape as the Lambda
    // direct-invoke contract.
    // `delete` doesn't take a body arg in our client; pass via options.
    return api.delete(`${BASE}/${encodeURIComponent(instanceId)}`, { body: JSON.stringify({ confirm: true }) });
  },

  /**
   * Toggle the in-container agent-ops scheduler for a CLOUD app. Sets the
   * per-app `agentOpsEnabled` flag and re-deploys the task so the running
   * container picks up AGENT_OPS_SCHEDULER_ENABLED. When off, the app keeps
   * serving (and `zibby app run` still works) but agent-ops stops its
   * autonomous scheduled runs.
   * @param {string} instanceId
   * @param {boolean} enabled
   * @returns {Promise<{ instanceId, agentOpsEnabled }>}
   */
  setAgentOps(instanceId, enabled) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.post(`${BASE}/${encodeURIComponent(instanceId)}/agent-ops`, { enabled: !!enabled });
  },

  /**
   * Destroy a SOLO-mode app. Solo apps run on a dedicated EC2 (not
   * Fargate), keyed by `appSlug` (not instanceId), and live under
   * DELETE /apps/solo/{slug}. This path terminates the VM, releases its
   * Elastic IP, and deletes the per-app Route53 A record — the cloud
   * `destroy(instanceId)` path does NONE of that for a solo row, so
   * routing one through it ORPHANS the VM (billed) + leaves dangling
   * DNS. Data buckets (Litestream backups) are RETAINED by design.
   * @param {string} slug  the app's `appSlug`
   */
  destroySolo(slug) {
    if (!slug) return Promise.reject(new Error('slug is required'));
    return api.delete(`${BASE}/solo/${encodeURIComponent(slug)}`);
  },

  /**
   * Toggle the agent-ops scheduler for a SOLO app (keyed by appSlug).
   * Persists the flag and, when the VM is running, restarts the agent-ops
   * service via SSM so it picks up the change.
   * @param {string} slug
   * @param {boolean} enabled
   * @returns {Promise<{ appSlug, agentOpsEnabled }>}
   */
  setSoloAgentOps(slug, enabled) {
    if (!slug) return Promise.reject(new Error('slug is required'));
    return api.post(`${BASE}/solo/${encodeURIComponent(slug)}/agent-ops`, { enabled: !!enabled });
  },

  // ── solo (dedicated EC2) ─────────────────────────────────────────────
  /**
   * Current status of a SOLO-mode app, keyed by `appSlug` (solo apps run
   * on a dedicated EC2 and are NOT in the cloud instanceId-keyed table —
   * `getInstance(instanceId)` won't find them). Backed by
   * GET /apps/solo/{slug}/status. Returns the lifecycle row:
   *   { appSlug, phase, detail, updatedAt, status, pausedReason, pausedAt,
   *     tier, instanceId, instanceType, domain, region, framework,
   *     monthlyUSD, s3Buckets }
   * Note: the status payload does NOT carry the live `publicIp` — only the
   * canonical `domain` (<slug>.apps.zibby.dev). The detail page derives the
   * public URL from `domain`.
   * @param {string} slug  the app's appSlug
   */
  getSoloStatus(slug) {
    if (!slug) return Promise.reject(new Error('slug is required'));
    return api.get(`${BASE}/solo/${encodeURIComponent(slug)}/status`);
  },

  /**
   * Fetch LIVE instance logs for a SOLO-mode app. Solo apps run agent-ops
   * as a systemd unit (`agent-opsd.service`) under journald on a dedicated
   * EC2 — there's no CloudWatch group, so the backend reads journald over
   * SSM and returns the SAME `lines: string[]` shape the cloud LogsTab uses.
   *
   * LIVE only: a stopped/paused VM is unreachable via SSM, so the backend
   * returns `{ lines: [], live: false, reason, note }` (200, not an error).
   * No load-older pagination — journald returns a bounded tail.
   *
   * @param {string} slug
   * @param {{ lines?: number, filter?: string }} [opts]
   * @returns {Promise<{ lines: string[], live: boolean, reason?: string, note?: string, nextToken: null, hasMore: false }>}
   */
  getSoloLogs(slug, { lines = 200, filter } = {}) {
    if (!slug) return Promise.reject(new Error('slug is required'));
    const qs = new URLSearchParams({ lines: String(lines) });
    if (filter && filter.trim()) qs.set('filter', filter.trim());
    return api.get(`${BASE}/solo/${encodeURIComponent(slug)}/logs?${qs.toString()}`);
  },

  // ── Dedicated Static IP addon (solo apps only) ──────────────────────
  /**
   * Current static-IP addon state for a solo app:
   *   { appSlug, enabled, status, staticIp, pricing: { priceUSD,
   *     billingPeriod, perMinuteMilliCents, ... } }
   * @param {string} slug  the app's appSlug
   */
  getStaticIpStatus(slug) {
    if (!slug) return Promise.reject(new Error('slug is required'));
    return api.get(`${BASE}/solo/${encodeURIComponent(slug)}/addons/static-ip`);
  },
  /**
   * Enable the dedicated static IP addon for a solo app. Credit-gated
   * server-side — a 402 (err.status===402, err.body has requiredMilliCents
   * / balanceMilliCents) means top up first. Per-minute charges begin now.
   * @param {string} slug
   */
  enableStaticIp(slug) {
    if (!slug) return Promise.reject(new Error('slug is required'));
    return api.post(`${BASE}/solo/${encodeURIComponent(slug)}/addons/static-ip`, {});
  },
  /**
   * Disable the dedicated static IP addon. The IP is released on the next
   * pause (and may change on resume); per-minute charges stop immediately.
   * @param {string} slug
   */
  disableStaticIp(slug) {
    if (!slug) return Promise.reject(new Error('slug is required'));
    return api.delete(`${BASE}/solo/${encodeURIComponent(slug)}/addons/static-ip`);
  },

  /**
   * Fetch a page of the instance's logs from CloudWatch. Backed by
   * FilterLogEvents over a per-instance stream prefix so results span
   * ALL ECS tasks for the instance (past + present, restart-survival).
   *
   * Pagination: pass `cursor` (the `nextToken` returned by a prior call)
   * to fetch the previous page (older lines). The frontend uses this
   * for infinite-scroll-to-top.
   *
   * Filtering: `filter` is forwarded to CloudWatch's filterPattern (a
   * plain substring also works). Empty/whitespace = no filter.
   *
   * @param {string} instanceId
   * @param {{ lines?: number, container?: 'agent-ops' | 'app', cursor?: string|null, filter?: string }} [opts]
   * @returns {Promise<{ instanceId, container, lines: string[], nextToken: string|null, hasMore: boolean }>}
   */
  getLogs(instanceId, { lines = 200, container, cursor, filter } = {}) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    const qs = new URLSearchParams({ lines: String(lines) });
    if (container) qs.set('container', container);
    if (cursor) qs.set('cursor', cursor);
    if (filter && filter.trim()) qs.set('filter', filter.trim());
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}/logs?${qs.toString()}`);
  },

  /**
   * List the instance's structured agent run records (newest first).
   * Backed by the `zibby-{stage}-app-runs` DynamoDB table the agent-ops
   * daemon pushes to after every finished run — so this is independent
   * of CloudWatch retention and surfaces failed/timed-out runs too
   * (which emit no `conversation complete` log line).
   *
   * Heavy fields (systemPrompt / userPrompt / result) are omitted from
   * the list payload — fetch a single run via `getRun` to expand.
   *
   * Pagination: pass `cursor` (the `nextToken` returned by a prior call)
   * to fetch the next page of older runs.
   *
   * @param {string} instanceId
   * @param {{ limit?: number, cursor?: string|null }} [opts]
   * @returns {Promise<{ instanceId, runs: object[], nextToken: string|null, hasMore: boolean }>}
   */
  listRuns(instanceId, { limit = 50, cursor } = {}) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    const qs = new URLSearchParams({ limit: String(limit) });
    if (cursor) qs.set('cursor', cursor);
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}/runs?${qs.toString()}`);
  },

  /**
   * Fetch a single agent run record in full — including the heavy
   * fields the list omits (`systemPrompt`, `userPrompt`, `result`).
   * Used to lazily populate an expanded row in the Agent activity tab.
   *
   * @param {string} instanceId
   * @param {string} runId
   * @returns {Promise<object>} the full run record
   */
  getRun(instanceId, runId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    if (!runId) return Promise.reject(new Error('runId is required'));
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}/runs/${encodeURIComponent(runId)}`);
  },

  /**
   * Live EFS usage for the instance. Backed by CloudWatch's AWS/EFS
   * StorageBytes metric (per-AccessPoint dimension; published ~every
   * 15min by EFS so brand-new deploys return `usedBytes: null` until
   * the first sample lands).
   *
   * @param {string} instanceId
   * @returns {Promise<{ instanceId, usedBytes: number|null, storageClass: string, sampleAt: string|null }>}
   */
  getStorage(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}/storage`);
  },

  /**
   * Live resource usage time-series for the instance. Backed by CloudWatch
   * Container Insights (CPU/memory/network/disk-io) + EFS StorageBytes
   * (disk). Brand-new deploys may return `current: null` / empty `series`
   * until the first sample lands — the Overview charts render a
   * "Collecting data…" overlay in that window.
   *
   *   rangeMins, periodSec
   *   cpu:    { current: number|null (%), series: [{ t, v }] }
   *   memory: { current: number|null (%), series: [{ t, v }], limitMiB }
   *   network:{ rx: [{ t, v: bytes/sec }], tx: [{ t, v }] }
   *   diskIo: { read: [{ t, v: bytes/sec }], write: [{ t, v }] }
   *   disk:   { usedBytes: number|null, totalBytes: number|null }
   *   tier, cpuLimit, memoryLimitMiB
   *
   * @param {string} instanceId
   * @returns {Promise<{ cpu, memory, network, diskIo, disk, tier, cpuLimit, memoryLimitMiB }>}
   */
  getMetrics(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}/metrics`);
  },

  /**
   * Change THIS app's resource tier (light / standard / xl). Resize is a
   * brief restart — the backend re-registers the per-instance task def with
   * the new cpu/memory and force-redeploys the ECS Service (EFS preserved),
   * flipping the row to `upgrading`. App must be running.
   *
   * @param {string} instanceId
   * @param {'light'|'standard'|'xl'} tier
   * @returns {Promise<{ status: 'upgrading', tier, cpu, memory }>}
   */
  changeTier(instanceId, tier) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    if (!tier) return Promise.reject(new Error('tier is required'));
    return api.patch(`${BASE}/${encodeURIComponent(instanceId)}/resources`, { tier });
  },

  /**
   * Start a DATA EXPORT for the instance. Launches a one-shot read-only
   * Fargate task that tars the instance's EFS data and uploads it to S3.
   * Returns immediately; poll `getExport` for readiness.
   *
   * @param {string} instanceId
   * @returns {Promise<{ exportId: string, status: 'preparing' }>}
   */
  exportData(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.post(`${BASE}/${encodeURIComponent(instanceId)}/export`, {});
  },

  /**
   * Poll the status of the instance's most recent data export. Once the
   * tarball lands in S3 the backend returns a short-TTL (~15 min) presigned
   * download URL.
   *
   *   { status: 'none' }                              never requested
   *   { status: 'preparing', exportId }               task still running
   *   { status: 'ready', url, expiresAt, exportId }   ready to download
   *
   * @param {string} instanceId
   * @returns {Promise<{ status: string, url?: string, expiresAt?: string, exportId?: string|null }>}
   */
  getExport(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}/export`);
  },

  /**
   * In-place upgrade: swap the agent-ops container image, preserving EFS
   * data + bridgeToken + ALB wiring. Re-registers the per-instance task
   * def with a new revision, then force-redeploys the ECS Service.
   *
   * `version` (optional) overrides the agent-ops image tag. Without it,
   * the upgrade picks up whatever's in the AppsFleet base task def in
   * SSM (which CDK has bumped).
   *
   * @param {string} instanceId
   * @param {{ version?: string }} [opts]
   * @returns {Promise<{ instanceId, status: 'upgrading', taskDefinitionArn, imageOverride: string|null }>}
   */
  upgrade(instanceId, { version } = {}) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    const qs = version ? `?version=${encodeURIComponent(version)}` : '';
    return api.post(`${BASE}/${encodeURIComponent(instanceId)}/upgrade${qs}`);
  },

  /**
   * Safe, read-only view of the per-instance env block. Drives the
   * "ENV" tab on AppDetailPage.
   *
   * Privacy: backend allow-lists which keys are returned at all, and
   * masks every sensitive value to its 6-char suffix. The plaintext of
   * CLAUDE_CODE_OAUTH_TOKEN / ANTHROPIC_API_KEY NEVER crosses this wire.
   *
   * @param {string} instanceId
   * @returns {Promise<{ instanceId, identity: Array<{key,value}>, credentials: Array<{key,present,suffix}> }>}
   */
  getEnv(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}/env`);
  },

  /**
   * Rotate the per-instance Claude credential to whatever is currently
   * stored in the user's workspace-credentials. Backend re-reads the
   * encrypted row, re-registers the per-instance task def with the new
   * env, and force-redeploys the ECS Service. EFS preserved.
   *
   * @param {string} instanceId
   * @returns {Promise<{ instanceId, mode, taskDefinitionArn, message }>}
   */
  updateCredential(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.post(`${BASE}/${encodeURIComponent(instanceId)}/credentials`, {});
  },

  /**
   * Edit THIS app's AI agent (provider + model + token). Mirrors the
   * per-pipeline AgentEditModal. The backend snapshots provider/model/
   * encrypted-token on the row and force-redeploys the ECS instance so it
   * picks up the change WITHOUT losing EFS.
   *
   * Token handling: pass the masked value back untouched to KEEP the
   * current token (the backend ignores any value containing '...'); pass a
   * fresh token to replace it. Required when switching to a provider with
   * no stored token + no workspace cred (backend 428s otherwise).
   *
   * @param {string} instanceId
   * @param {{ provider?: 'claude'|'codex', model?: string|null, token?: string }} body
   * @returns {Promise<{ instanceId, provider, model, agentToken, taskDefinitionArn, message }>}
   */
  setAgent(instanceId, body = {}) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.post(`${BASE}/${encodeURIComponent(instanceId)}/agent`, body);
  },

  /**
   * Explicit Start/Restart for a stopped / paused / failed app. Bumps the
   * ECS service back to desiredCount=1 (forceNewDeployment) and flips the
   * row to running. Idempotent: an already-live app returns
   * { started: false, reason: 'already-running' }. If the service no longer
   * exists the backend 409s with { reason: 'service-missing' } — the caller
   * should surface "redeploy the app".
   *
   * @param {string} instanceId
   * @returns {Promise<{ started: boolean, instanceId, status?, reason? }>}
   */
  start(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.post(`${BASE}/${encodeURIComponent(instanceId)}/start`, {});
  },

  /**
   * Explicit Stop (PAUSE) for a live app. Scales the ECS service to
   * desiredCount=0 (cloud) / StopInstances (solo) — KEEPS the service so a
   * subsequent start() resumes it; this is NOT destroy. Idempotent: an
   * already-stopped app returns { stopped: false, reason: 'already-stopped' }.
   *
   * @param {string} instanceId
   * @returns {Promise<{ stopped: boolean, instanceId, status?, reason? }>}
   */
  stop(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.post(`${BASE}/${encodeURIComponent(instanceId)}/stop`, {});
  },

  /**
   * Set / rotate / clear the optional Caddy reverse-proxy auth gate in front
   * of the app's public URL. PATCH semantics: the row holds ONE auth method.
   *
   *   { authType: 'basic', authUser, authPassword }   require HTTP Basic
   *   { authType: 'token', authToken? }               require a bearer token
   *                                                    (omit authToken → backend generates)
   *   { authType: 'none' }                            remove the gate
   *
   * Triggers a task restart (the Caddy sidecar comes up alongside the app),
   * so the backend returns 202. Response carries the redacted `auth` block;
   * for a freshly-generated token the plaintext is present ONCE as `auth.token`.
   * App must be running (409 if stopped/destroyed/failed).
   *
   * @param {string} instanceId
   * @param {{ authType:'basic'|'token'|'none', authUser?, authPassword?, authToken? }} body
   * @returns {Promise<{ instanceId, auth: { type, user?, tokenLast4?, token?, generated? }, message }>}
   */
  updateAuth(instanceId, body = {}) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.patch(`${BASE}/${encodeURIComponent(instanceId)}/auth`, body);
  },

  /**
   * Change THIS app's vanity subdomain after creation. The backend
   * re-validates the label (reserved/charset/length), checks availability
   * (409 if taken), modifies the app's OWN ALB listener-rule host-header
   * to the new subdomain (skipped + persisted only when the app has no
   * registered rule yet — e.g. stopped), and persists the new fqdn on the
   * row. A label equal to the current subdomain is a 200 no-op.
   *
   * @param {string} instanceId
   * @param {string} subdomain  the LABEL (part before .apps.zibby.dev)
   * @returns {Promise<{ instanceId, subdomain, url }>}
   */
  updateSubdomain(instanceId, subdomain) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.patch(`${BASE}/${encodeURIComponent(instanceId)}/subdomain`, { subdomain });
  },

  /**
   * Attach a custom (bring-your-own) domain to THIS app. Cloud apps only
   * (solo apps 400). The backend provisions an ACM cert + ALB host-rule and
   * returns the two DNS records the user must publish at their provider:
   * a `validation` CNAME (proves ownership) and a `routing` CNAME (points the
   * domain at the app). Status starts `pending` until DNS validates.
   *
   * Errors: 400 (invalid domain / solo app), 409 (domain already taken).
   *
   * @param {string} instanceId
   * @param {string} domain  the fqdn the user owns (e.g. app.yourdomain.com)
   * @returns {Promise<{ instanceId, domain, status, dns: { validation:{type,name,value}, routing:{type,name,value} } }>}
   */
  setCustomDomain(instanceId, domain) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.post(`${BASE}/${encodeURIComponent(instanceId)}/domain`, { domain });
  },

  /**
   * Fetch the current custom-domain state for THIS app. Poll while
   * `status === 'pending'` to flip to `active` once DNS validates.
   *
   * @param {string} instanceId
   * @returns {Promise<{ domain, status: 'none'|'pending'|'active'|'failed', dns?: { validation:{type,name,value}, routing:{type,name,value} } }>}
   */
  getCustomDomain(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}/domain`);
  },

  /**
   * Detach the custom domain from THIS app (tears down the ALB host-rule /
   * cert). Idempotent.
   *
   * @param {string} instanceId
   * @returns {Promise<{ ok: true }>}
   */
  removeCustomDomain(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.delete(`${BASE}/${encodeURIComponent(instanceId)}/domain`);
  },
};
